import React, { useState, useEffect, useRef } from "react";
import ProblemNotes from "./TextBox";
import { useTranslation } from "react-i18next";
import { sendOtp, verifyOtp } from "../Services/api";
import { toast } from "react-toastify";
import Loader from './Loader';

export default function OTPVerification({ mode, onSuccess }) {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [notes, setNotes] = useState("");
  const [sent, setSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const logo = process.env.REACT_APP_HOSPITAL_LOGO;
  const hospitalName = process.env.REACT_APP_PAY_HOSPITAL_NAME;
  const OTP_LENGTH = 6;
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(""));
  const inputsRef = useRef([]);
  const hasFocusedRef = useRef(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sent && !otpVerified && !hasFocusedRef.current) {
      hasFocusedRef.current = true;
      setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 50);
    }
  }, [sent, otpVerified]);


  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldown]);

  const sendOTP = async () => {
    if (!/^\d{10}$/.test(phone)) {
      toast.warn("Please enter a valid 10-digit phone number");
      return;
    }

    const normalizedPhone = phone.startsWith("+91") ? phone : `+91${phone}`;

    try {
      setSending(true);

      const data = await sendOtp(normalizedPhone);
      if (data?.message || data?.success) {
        setSent(true);
        setCooldown(300);
        toast.success(data.message || "OTP sent successfully!");
      } else {
        toast.error(data?.error || data?.message || "Failed to send OTP");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Error sending OTP"
      );
    }
    finally {
      setSending(false);
    }
  };

  const verifyOTP = async (enteredOtp) => {
    if (!enteredOtp || enteredOtp.length !== OTP_LENGTH) {
      toast.warn("Please enter OTP");
      return;
    }

    const normalizedPhone = phone.startsWith("+91") ? phone : `+91${phone}`;

    try {
      setVerifying(true);
      setLoadingMessage("Verifying OTP...");
      setLoading(true);
      const data = await verifyOtp(normalizedPhone, enteredOtp);
      if (data?.status === "success" || data?.success) {
        const verifiedAt = new Date().toISOString();

        setOtpVerified(true);
        localStorage.setItem("otpVerified", "true");
        localStorage.setItem("phoneNumber", phone);
        localStorage.setItem("otpVerifiedAt", verifiedAt);
        localStorage.setItem("lastActiveAt", Date.now.toString());


        toast.success(data?.message || "OTP verified!");
        onSuccess(notes, phone);
      } else {
        toast.error(data?.error || data?.message || "Invalid OTP");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Error verifying OTP"
      );
    } finally {
      setVerifying(false);
            setLoading(false);

    }
  };

  const proceed = () => {
    if (!notes.trim()) {
      toast.warn(t("otp.enterNotes") || "Please enter notes.");
      return;
    }
    onSuccess(notes, phone);
  };

  return (

    <div className="p-6 sm:p-8 rounded-2xl max-w-md mx-auto mt-12
                bg-white dark:bg-gray-900 shadow-xl
                border border-gray-200 dark:border-gray-700
                transition-all duration-500">
      {loading && <Loader message={loadingMessage} />}

      <div className="text-center mb-6">
        {/* App Logo */}
        <img
          // src={"Images/TriloLogo_TRSP.png"}
          src={logo}
          alt={t("login.appName")}
          className="mx-auto h-16 w-auto"
        />
        {/* Hospital Name */}
        {/* <h2 className="mt-3 text-lg font-semibold text-gray-700 dark:text-gray-200">
          {hospitalName}
        </h2> */}
        <h1
          className="text-center
             text-xl sm:text-2xl md:text-3xl
             font-semibold tracking-tight
             text-[#0F4C81] dark:text-[#6FB4FF]
             truncate max-w-full leading-tight"
          title={hospitalName}
        >
          {hospitalName}
        </h1>
        {/* App Name */}
        {/* <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mt-3">
          
          {t("login.appName")}
        </h1> */}

        {/* Subtitle */}
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
          {t("login.subtitle")}
        </p>
      </div>

      <div className="mb-4">
        <label className="block font-semibold mb-1 text-gray-700 dark:text-gray-200">
          {t("otp.mobileNumber") || "Mobile Number"}
        </label>
        <input
          type="tel"                    // <-- changed from "text" to "tel"
          inputMode="numeric"           // <-- hints numeric keyboard
          pattern="[0-9]*"              // <-- ensures only digits on some browsers
          placeholder={t("otp.mobilePlaceholder") || "Enter phone number"}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full p-2 rounded border border-gray-300 dark:border-gray-600
                     focus:ring-2 focus:ring-cyan-400 dark:focus:ring-cyan-500
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                     transition-colors duration-300"
        />
      </div>

      <div className="mb-6 relative overflow-hidden rounded-lg">
        <button
          className={`w-full py-3 rounded-lg font-semibold text-white transition-all duration-300
      ${otpVerified || cooldown > 0 || sending
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-md hover:shadow-lg"
            }`}
          onClick={sendOTP}
          disabled={otpVerified || cooldown > 0 || sending}
        >
          {sending ? "Sending OTP..." : cooldown > 0 ? `Send OTP (${cooldown}s)` : "Send OTP"}
        </button>

        {sending && (
          <div className="absolute left-0 bottom-0 h-1 w-full bg-white/20 overflow-hidden">
            <div className="h-full w-1/2 bg-white/70 animate-loading rounded-full" />
          </div>
        )}
      </div>




      {sent && !otpVerified && (
        <div className="mb-6">
          <label className="block font-semibold mb-2 text-gray-700 dark:text-gray-200 text-center">
            {t("otp.enterOtpLabel") || "Enter OTP"}
          </label>

          <div className="flex flex-wrap justify-center gap-1 sm:gap-2 mb-4 px-2 max-w-full">

            {otpDigits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputsRef.current[index] = el)}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (!val) return;

                  const newOtp = [...otpDigits];
                  newOtp[index] = val;
                  setOtpDigits(newOtp);

                  if (index < OTP_LENGTH - 1) {
                    inputsRef.current[index + 1]?.focus();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Backspace") {
                    const newOtp = [...otpDigits];
                    if (newOtp[index]) {
                      newOtp[index] = "";
                      setOtpDigits(newOtp);
                    } else if (index > 0) {
                      inputsRef.current[index - 1]?.focus();
                    }
                  }
                }}
                className="w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-semibold rounded-lg border border-gray-300 dark:border-gray-600
           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
           focus:ring-2 focus:ring-green-400 dark:focus:ring-green-500 transition"

              />
            ))}
          </div>

          <div className="relative overflow-hidden rounded-lg">
            <button
              onClick={() => verifyOTP(otpDigits.join(""))}
              disabled={verifying || otpDigits.some((d) => !d)}
              className={`w-full py-3 rounded-lg font-semibold text-white transition-all
          ${verifying || otpDigits.some((d) => !d)
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-500 hover:bg-green-600"
                }`}
            >
              {verifying ? "Verifying OTP..." : "Verify"}
            </button>

            {verifying && (
              <div className="absolute left-0 bottom-0 h-1 w-full bg-white/20 overflow-hidden">
                <div className="h-full w-1/2 bg-white/70 animate-loading rounded-full" />
              </div>
            )}
          </div>
        </div>
      )}


      {/* {otpVerified && (
        <>
          <ProblemNotes value={notes} onChange={setNotes} />
          <div className="text-center mt-4">
            <button
              onClick={proceed}
              className="px-8 py-2 rounded-md font-semibold
                         bg-green-500 hover:bg-green-600 text-white transition-all"
            >
              {t("otp.proceed") || "Proceed"}
            </button>
          </div>
        </>
      )} */}
    </div>
  );
}
